import {useMemo} from 'react'
import {NavigateOptions, useSearchParams as useRRSearchParams} from 'react-router-dom'
import {TypeOf, ZodObject, ZodRawShape} from 'zod'

const serializeValue = (value: unknown) => (value instanceof Date ? value.toISOString() : String(value))

type RawParams = Record<string, string | string[]>

/**
 * This abstraction follow the following constraints:
 *
 * 1. Array fields keys must end with `[]`
 * 2. Array fields values must be comma separated
 * 3. Date values are serialized to ISO strings
 */
export const sp = {
    parse(searchParams: URLSearchParams): RawParams {
        return [...searchParams.entries()].reduce<RawParams>((acc, [key, value]) => {
            if (key.endsWith('[]')) {
                acc[key.replace('[]', '')] = value.split(',')
            } else {
                acc[key] = value
            }
            return acc
        }, {})
    },
    serialize<T extends Record<string, unknown>>(params: T): URLSearchParams {
        const searchParams = new URLSearchParams()
        Object.entries(params).forEach(([key, value]) => {
            if (value == null || value === '') {
                return
            }

            if (Array.isArray(value)) {
                if (value.length > 0) {
                    searchParams.set(`${key}[]`, value.map(serializeValue).join(','))
                }
                return
            }

            searchParams.set(key, serializeValue(value))
        })
        return searchParams
    },
    stringify<T extends Record<string, unknown>>(params: T) {
        return this.serialize(params).toString()
    },
    with<Z extends ZodObject<ZodRawShape>>(schema: Z) {
        type T = TypeOf<Z>
        const parse = (searchParams: URLSearchParams): T => {
            const parsed = this.parse(searchParams)
            return schema.parse(parsed)
        }
        const serialize = this.serialize.bind(this)
        const stringify = this.stringify.bind(this)
        return {parse, serialize, stringify} as const
    }
} as const

export const useSearchParams = <Z extends ZodObject<ZodRawShape>>(schema: Z) => {
    const [searchParams, setSearchParams] = useRRSearchParams()
    type T = TypeOf<Z>

    return useMemo(() => {
        const parser = sp.with(schema)
        const params = parser.parse(searchParams)
        const setParams = (
            paramsOrUpdaterFn: Partial<T> | ((params: T) => Partial<T>),
            navigateOptions?: NavigateOptions
        ) => {
            const newParams =
                typeof paramsOrUpdaterFn == 'function' ? paramsOrUpdaterFn(params) : {...params, ...paramsOrUpdaterFn}
            const serialized = parser.serialize(newParams)
            setSearchParams(serialized, {replace: true, ...navigateOptions})
        }
        return [params, setParams] as const
    }, [searchParams, setSearchParams])
}
